import {useRef, useState} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {toast} from 'react-toastify';
import {
  getConvoIdSupervisor,
  getmessageListSupervisor,
  getQuestionSupervisor,
  setConvoIdSupervisor,
  setmessageListSupervisor,
  setQuestionSupervisor,
  updateMessageListSupervisor,
  patchMessageListSupervisor,
  clearChatSliceSupervisor,
} from '../../../store/SupervisorChatSlice';
import {useAppDispatch, useAppSelector} from '../../../store/Store';
import generateUniqueId from '../../../Utils/uuidGeneration';
import {askSupervisor, askSupervisorMemory} from './supervisorChatApi';
import {applySupervisorStreamEvent, createLiveTraceState} from './supervisorLiveTrace';

const REQUEST_TIMEOUT_MS = 360000;

// Chat hook dedicated to the /raas2 page's planner/orchestrator
// backend. Kept separate from `useChatGenie` (used by /chatGenie) since the
// two APIs return differently-shaped payloads.
const useSupervisorChat = () => {
  const messages = useAppSelector<any>(getmessageListSupervisor) || [];
  const dispatch = useAppDispatch();
  const controllerRef = useRef<AbortController | null>(null);
  const storedSessionId = useAppSelector(getConvoIdSupervisor);
  const Question = useAppSelector(getQuestionSupervisor);
  const {authState} = useOktaAuth();
  const userEmail = authState?.accessToken?.claims?.email as string | undefined;
  const [isMemoryEnabled, setIsMemoryEnabled] = useState(false);
  // "Agent" header toggle: sends engine: 'nl2sql' instead of the default
  // 'genie' in the streaming ask payload.
  const [isAgentEnabled, setIsAgentEnabled] = useState(false);
  // Memory-mode-only conversation state: absent on the first call, then
  // carried forward from each response so the backend can maintain
  // multi-turn memory. Kept out of Redux since it's specific to this API
  // and resets whenever the chat is cleared.
  const memorySessionIdRef = useRef<string | null>(null);
  const lastReformulatedQueryRef = useRef<string | null>(null);

  const toggleMemory = (checked: boolean) => {
    setIsMemoryEnabled(checked);
  };

  const toggleAgent = (checked: boolean) => {
    setIsAgentEnabled(checked);
  };

  const handleSendQuestionGenie = async (question: string = Question) => {
    if (question.trim() === '') return;
    const trimmedQuestion = question.trim();
    const time = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const startTime = Date.now();
    const reqId = generateUniqueId();
    const newMessage = {sender: 'user', text: trimmedQuestion, time};
    const newResponse = {
      sender: 'bot',
      isLoading: true,
      text: '',
      time,
      question: trimmedQuestion,
      request_id: reqId,
    };
    dispatch(setmessageListSupervisor([...messages, newMessage, newResponse]));

    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    controllerRef.current = new AbortController();

    // Reuse the same session id for the whole conversation so the backend
    // can maintain multi-turn context; a fresh one is minted on first send
    // (or after "New chat", which clears convoIdSupervisor).
    const sessionId = storedSessionId || generateUniqueId();
    if (!storedSessionId) {
      dispatch(setConvoIdSupervisor(sessionId));
    }

    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), REQUEST_TIMEOUT_MS),
    );

    try {
      if (isMemoryEnabled) {
        const res: any = await Promise.race([
          askSupervisorMemory(
            trimmedQuestion,
            controllerRef.current.signal,
            memorySessionIdRef.current,
            lastReformulatedQueryRef.current,
          ),
          timeout,
        ]);
        memorySessionIdRef.current = res?.session_id || memorySessionIdRef.current;
        lastReformulatedQueryRef.current =
          res?.reformulated_query || lastReformulatedQueryRef.current;
        const timeTaken = Date.now() - startTime;
        const baseText: string =
          res?.additional_text_memory || res?.additional_text || '';
        const isFailed = Boolean(res?.status) && res.status !== 'COMPLETED';
        const displayText = baseText.length > 0
          ? baseText
          : 'No answer could be produced for this question.';
        const answeredFromMemory = res?.llm_validation === true;

        dispatch(
          updateMessageListSupervisor({
            requestId: reqId,
            finalBotMessage: {
              sender: 'bot',
              text: displayText,
              content: displayText,
              time,
              request_id: reqId,
              question: trimmedQuestion,
              isError: isFailed,
              timeTaken,
              answeredFromMemory,
              // Only surface the SQL used when the answer wasn't served
              // straight from memory (no query was run in that case).
              sqlData: !answeredFromMemory ? res?.sql_query || [] : undefined,
              traceId: res?.trace_id || '',
            },
          }),
        );
        return;
      }

      let liveTrace = createLiveTraceState();
      const res: any = await Promise.race([
        askSupervisor(
          trimmedQuestion,
          sessionId,
          userEmail,
          controllerRef.current.signal,
          (eventName, data) => {
            liveTrace = applySupervisorStreamEvent(liveTrace, eventName, data);
            dispatch(
              patchMessageListSupervisor({requestId: reqId, patch: {liveTrace}}),
            );
          },
          isAgentEnabled ? 'nl2sql' : 'genie',
        ),
        timeout,
      ]);
      const timeTaken = Date.now() - startTime;
      dispatch(setConvoIdSupervisor(res?.session_id || sessionId));

      const finalResult = res?.final_result || {};
      const executiveSummary = finalResult.executive_summary || {};
      const charts = Array.isArray(executiveSummary.charts) ? executiveSummary.charts : [];
      const tableMarkdown: string = executiveSummary.data || '';
      const baseText: string = executiveSummary.response || '';
      const outcome: string | undefined = finalResult.outcome;
      const caveats: string[] = Array.isArray(finalResult.coverage_caveats)
        ? finalResult.coverage_caveats
        : [];
      const detailedFindings = Array.isArray(finalResult.detailed_findings)
        ? finalResult.detailed_findings
        : [];
      const isAborted = res?.status === 'failed' || outcome === 'aborted';
      const hasContent = baseText.length > 0 || charts.length > 0 || tableMarkdown.length > 0;
      const displayText = hasContent
        ? baseText
        : 'No answer could be produced for this question.';

      dispatch(
        updateMessageListSupervisor({
          requestId: reqId,
          finalBotMessage: {
            sender: 'bot',
            text: displayText,
            content: displayText,
            data: tableMarkdown,
            charts,
            agentTrace: finalResult.agentTrace,
            contentBlocks: finalResult.contentBlocks,
            queryBlocks: finalResult.queryBlocks,
            detailedFindings,
            outcome,
            caveats,
            confidence: finalResult.overall_confidence,
            time,
            request_id: reqId,
            question: trimmedQuestion,
            isError: isAborted,
            timeTaken,
          },
        }),
      );
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred.';
      if (error?.code === 'ERR_CANCELED' || error?.name === 'AbortError') {
        errorMessage = 'Request cancelled by user';
      } else if (error?.message === 'Request timed out') {
        errorMessage =
          'The response is taking too long. Please try again or rephrase your question.';
        if (controllerRef.current) {
          controllerRef.current.abort();
          controllerRef.current = null;
        }
      } else if (!error?.response) {
        errorMessage = "Looks like the data didn't connect properly. A quick refresh should help.";
      } else if (error.response.status >= 500) {
        errorMessage = 'Unfortunately, I cannot serve you now. Please try again in some time.';
      } else if (error.response.status >= 400) {
        errorMessage = error.response.data?.message || 'There was an issue with your request.';
      }
      dispatch(
        updateMessageListSupervisor({
          requestId: reqId,
          finalBotMessage: {
            sender: 'bot',
            text: errorMessage,
            content: errorMessage,
            time,
            request_id: reqId,
            isLoading: false,
            timeTaken: Date.now() - startTime,
            question: trimmedQuestion,
            isError: true,
          },
        }),
      );
    } finally {
      dispatch(setQuestionSupervisor(''));
    }
  };

  const handleStopChat: React.MouseEventHandler<HTMLDivElement> = () => {
    const lastMessage = messages[messages.length - 1];
    dispatch(
      updateMessageListSupervisor({
        requestId: lastMessage?.request_id,
        finalBotMessage: {
          sender: 'bot',
          text: 'Request cancelled',
          content: 'Request cancelled',
          request_id: lastMessage?.request_id,
          isLoading: false,
          timeTaken: 0,
          question: lastMessage?.question,
          isError: true,
        },
      }),
    );
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    toast.info('Request cancelled by the user');
  };

  const handleClearChat: React.MouseEventHandler<HTMLElement> = () => {
    toast.info('Started a new chat.');
    dispatch(clearChatSliceSupervisor());
    memorySessionIdRef.current = null;
    lastReformulatedQueryRef.current = null;
  };

  return {
    handleSendQuestionGenie,
    question: Question,
    handleStopChat,
    handleClearChat,
    isMemoryEnabled,
    toggleMemory,
    isAgentEnabled,
    toggleAgent,
  };
};

export default useSupervisorChat;
